import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface Location {
  Id: number | null;
  LocationId: number | null;
  Location: string | null;
  Cost: number | null;
}

export interface Cost {
  Id: number | null;
  CostName: string | null;
  Cost: number | null;
  EventId: number | null;
}

export interface IOrderDetail {
  Id: number | null;
  ParticipantType: string | null;
  FirstName: string | null;
  LastName: string | null;
  Esquire: string | null;
  BarId: string | null;
  Email: string | null;
  Location: string | null;
  Cost: string | null;
  OrderId: number | null;
  EventId: string | null;
  MemberId: number | null;
  DECredits: number | null;
  DEEnchEthics: number | null;
  PACredits: number | null;
  PAEthics: number | null;
  NJCredits: number | null;
  NJEthics: number | null;
  AmountPaid: number | null;
  CouponAmount: number | null;
  TotalAmount: number | null;
  IsAttended: number | null;
  IsAttendenceUpdated: number | null;
  IsAttendenceInEditMode: boolean;
  IsGuest: boolean | null;
  DateCompleted : string | null;
}

export interface IEvent {
  Id: number;
  EventName: string | null;
  Description: string | null;
  UniqueEventId: string | null;
  EventType: string | null;
  EventCategory: string | null;
  StartDateTime: string | null;
  EndDateTime: string | null;
  TestDateTime: string | null;
  EarlyBirdBy: string | null;
  Available: number | null;
  AvailableOnline: number | null;
  SponsorName: string | null;
  RegistrationLink: string | null;
  ZoomMeetingID: string | null;
  ZoomRegLink: string | null;
  DECredits: number | null;
  DEEnchEthics: number | null;
  PACredits: number | null;
  PAEthics: number | null;
  NJCredits: number | null;
  NJEthics: number | null;
  Comments: string | null;
  MaterialLink: string | null;
  Locations: Location[];
  ChildEventId: number | null;
  ParentEventId: number | null;
  MemberCostPerHour: number | 0;
  NonMemberCostPerHour: number | 0;
  MemberCost: number | 0;
  NonMemberCost: number | 0;
  PACost: number | null;
  VideoURL: string | null;
  VideoCost: number | null;
  Costs: Cost[];
  OrderDetail: IOrderDetail[] | null;
  NumberOfAttendee: string | null;
}

// both event and order
export interface IMemberEvent {
  MemberId: number;
  EventName: string | null;
  OrderId: number | null;
  UniqueEventId: string | null;
  CreatedAt: string | null;
  StartDateTime: string | null;
  EndDateTime: string | null;
  EventId: number | null;
  EffectiveFiscalYearDateStart: string | null;
  MemberCode: string;
  Description: string;
  TotalAmount: number;
  AmountPaid: number;
  DueAmount: number;
  OrderStatus: number;
  CurrentYearSections: (number | null)[] | [] | undefined;
  SectionNames: string;
  InvoiceId: number;
  InvoiceUrl: string;
  DatePaid: string | null;
  EarnedDECredits: number;
  EarnedDEEEthics: number;
  EarnedPACredits: number;
  EarnedPAEEthics: number;
}

export interface IEventSearchParam {
  CurrentPage: number;
  Paginate: number;
  searchElement: string;
  SortBy: string;
  SortType: string;
  eventType: string;
  startDate?: string;
  endDate?: string;
  locationId?: number | null;
  sponsor?: string;
}

const EventSearchParam: IEventSearchParam = {
  CurrentPage: 1,
  Paginate: 10,
  searchElement: "",
  SortBy: "StartDateTime",
  SortType: "Desc",
  eventType: "",
};

// export interface IVideoEvent {
//   EventId: number | null;
//   OrderType: string | null;
//   Cost: number | null;
// }

const OrderDetail: IOrderDetail = {
  Id: null,
  ParticipantType: null,
  FirstName: null,
  LastName: null,
  BarId: null,
  Email: null,
  Esquire: null,
  Location: null,
  Cost: null,
  OrderId: null,
  EventId: null,
  MemberId: null,
  DECredits: null,
  DEEnchEthics: null,
  PACredits: null,
  PAEthics: null,
  NJCredits: null,
  NJEthics: null,
  IsAttended: null, // Actual attendance
  IsAttendenceUpdated: null,
  IsAttendenceInEditMode: false, // will always be false initially from API
  IsGuest: null,
  DateCompleted: null,
  AmountPaid: null,
  TotalAmount: null,
  CouponAmount: null
};

const Event: IEvent = {
  Id: 0,
  EventName: null,
  Description: null,
  UniqueEventId: null,
  EventType: null,
  EventCategory: null,
  StartDateTime: null,
  EndDateTime: null,
  TestDateTime: null,
  EarlyBirdBy: null,
  Available: 1,
  AvailableOnline: 1,
  SponsorName: null,
  RegistrationLink: null,
  ZoomMeetingID: null,
  ZoomRegLink: null,
  DECredits: null,
  DEEnchEthics: null,
  PACredits: null,
  PAEthics: null,
  NJCredits: null,
  NJEthics: null,
  Comments: null,
  MaterialLink: null,
  ChildEventId: null,
  ParentEventId: 0,
  VideoURL: null,
  VideoCost: null,
  MemberCostPerHour: 0,
  NonMemberCostPerHour: 0,
  MemberCost: 0,
  NonMemberCost: 0,
  PACost: 0,
  Locations: [],
  Costs: [],
  OrderDetail: [OrderDetail],
  NumberOfAttendee: null,
};

const initialState: { event: IEvent; eventSearchParam: IEventSearchParam } = {
  event: Event,
  eventSearchParam: EventSearchParam,
};

type BodyType = {
  key: string;
  value: string | number;
};

export type EventDetailBodyType = {
  key: string;
  value: string | boolean | number;
  index: number;
};
type CostType = {
  key: string;
  value: Cost | Location;
};

// type CostArrayType = {
//   key: string;
//   value: Cost[];
// };

export const eventApiSlice = createSlice({
  name: "eventapislice",
  initialState,
  reducers: {
    resetEvent: () => {
      return initialState;
    },
    assignEvent: (state: any, action: PayloadAction<IEvent>) => {
      console.log("assignEvent:", action.payload);
      const newState = {
        ...state,
        event: action.payload,
      };
      return newState;
    },
    assignEventSearchParam: (
      state: any,
      action: PayloadAction<IEventSearchParam>
    ) => {
      console.log("assignEvent:", action.payload);
      const newState = {
        ...state,
        eventSearchParam: action.payload,
      };
      return newState;
    },
    createEvent: (state: any, action: PayloadAction<IEvent>) => {
      state.eventapislice = action.payload;
    },
    updateEvent: (state: any, action: PayloadAction<BodyType>) => {
      console.log("key&value:", action.payload.key, ":", action.payload.value);

      const newState = {
        ...state,
        event: {
          ...state.event,
          [action.payload.key]: action.payload.value,
        },
      };
      // Return the new state
      return newState;
    },
    updateEventNoFormat: (state: any, action: PayloadAction<BodyType>) => {
      console.log(
        "key&valueNoFormat:",
        action.payload.key,
        ":",
        action.payload.value
      );
      const newState = {
        ...state,
        [action.payload.key]: action.payload.value,
      };

      // Return the new state
      return newState;
    },
    clearVideoURL: (state: any) => {
      const newState = {
        ...state,
        event: {
          ...state.event,
          VideoURL: null,
        },
      };
      console.log("newState:", newState);
      // Return the new state
      return newState;
    },
    clearMaterialURL: (state: any) => {
      const newState = {
        ...state,
        event: {
          ...state.event,
          MaterialLink: null,
        },
      };

      // Return the new state
      return newState;
    },
    addCostInfo: (state: any, action: PayloadAction<CostType>) => {
      console.log("key&value:", action.payload.key, ":", action.payload.value);
      // console.log("key&value1:",state[action.payload.key]);
      // state[action.payload.key] = [...state.Costs, action.payload.value];

      const newState = {
        ...state,
        event: {
          ...state.event,
          [action.payload.key]: [
            ...state.event[action.payload.key],
            action.payload.value,
          ],
        },
      };
      return newState;
    },
    addLocationInfo: (state: any, action: PayloadAction<CostType>) => {
      // state[action.payload.key] = [...state.Locations, action.payload.value];

      const newState = {
        ...state,
        event: {
          ...state.event,
          [action.payload.key]: [
            ...state.event[action.payload.key],
            action.payload.value,
          ],
        },
      };
      return newState;
    },

    // for video event where dispatch is called on onChangeHandler.
    // updateCostInfo: (state: any, action: PayloadAction<CostArrayType>) => {
    //   console.log("key&value:", action.payload.key, ":", action.payload.value);
    //   // console.log("key&value1:",state[action.payload.key]);
    //   // state[action.payload.key] = [...state.Costs, action.payload.value];

    //   const newState = {
    //     ...state,
    //     event: {
    //       ...state.event,
    //       [action.payload.key]: action.payload.value,
    //     },
    //   };
    //   console.log("updated cost:", newState.event.Costs);
    //   return newState;
    // },
    deleteCostInfo: (state: any, action: PayloadAction<BodyType>) => {
      console.log("key&value:", action.payload.key, ":", action.payload.value);
      state.event[action.payload.key] = state.event[action.payload.key].filter(
        (item: Cost) => {
          return item.Id != action.payload.value;
        }
      );
    },
    deleteLocationInfo: (state: any, action: PayloadAction<BodyType>) => {
      console.log("key&value:", action.payload.key, ":", action.payload.value);
      state.event[action.payload.key] = state.event[action.payload.key].filter(
        (item: Location) => {
          return item.Id != action.payload.value;
        }
      );
    },
    resetArrayObjects: (state: any, action: PayloadAction<BodyType>) => {
      const newState = {
        ...state,
        event: {
          ...state.event,
          [action.payload.key]: [],
        },
      };
      // Return the new state
      return newState;
    },

    setEventCurrentPage: (state: any, action: PayloadAction<number>) => {
      state.eventSearchParam.CurrentPage = action.payload;
    },
    setEventPaginate: (state: any, action: PayloadAction<number>) => {
      state.eventSearchParam.Paginate = action.payload;
    },
    setSearchElement: (state: any, action: PayloadAction<string>) => {
      state.eventSearchParam.searchElement = action.payload;
    },
    setUpdateSortType: (state: any, action: PayloadAction<string>) => {
      state.eventSearchParam.SortType = action.payload;
    },
    setUpdateSortBy: (state: any, action: PayloadAction<string>) => {
      state.eventSearchParam.SortBy = action.payload;
    },

    updateEventList: () => {},
    deleteEvent: (state: any, action: PayloadAction<IEvent>) => {
      state.eventapislice = initialState;
    },

    updateOrderDetail: (
      state: any,
      action: PayloadAction<EventDetailBodyType>
    ) => {
      console.log("eventApiSlice-payload:", action.payload);

      const { key, value, index } = action.payload;

      // Ensure the key is 'OrderDetail' and index is within valid range
      if (
        // key === "OrderDetail" &&
        index >= 0 &&
        index < state.event.OrderDetail.length
      ) {
        // Create a new array by updating the DECredits value at the specified index
        const updatedOrderDetail = state.event.OrderDetail.map(
          (item: any, i: number) => {
            if (i === index) {
              return {
                ...item,
                [key]: value,
              };
            }
            return item;
          }
        );

        console.log("updatedOrderDetail:", updatedOrderDetail);
        // Return the updated state with the modified OrderDetail array
        return {
          ...state,
          event: {
            ...state.event,
            OrderDetail: updatedOrderDetail,
          },
        };
      }

      // Return the state as is if key or index is invalid
      return state;
    },
  },
});

export const {
  resetEvent,
  createEvent,
  updateEvent,
  clearVideoURL,
  clearMaterialURL,
  deleteEvent,
  addCostInfo,
  // updateCostInfo,
  deleteCostInfo,
  addLocationInfo,
  deleteLocationInfo,
  resetArrayObjects,
  assignEvent,
  assignEventSearchParam,
  updateEventNoFormat,
  setEventCurrentPage,
  setSearchElement,
  setUpdateSortType,
  setUpdateSortBy,
  updateOrderDetail,
} = eventApiSlice.actions;

export default eventApiSlice.reducer;
